import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { HugeiconsIcon } from "@hugeicons/react";
import Cancel01Icon from "@hugeicons/core-free-icons/Cancel01Icon";

// Modal — ported from src/components/Modal.jsx and rethemed onto `.ops-v2` tokens.
//
// The legacy component is genuinely good (accessible, backdrop-close, scroll-contained,
// no coupling to legacy state) so this keeps its structure. Three changes:
//   1. Colours move to --ops-* tokens; the legacy `bg-white-50` works only because
//      tailwind.config.js redefines `white` as a numbered scale.
//   2. `id="backdrop"` string matching is replaced with a ref-free target check that
//      can't collide if two modals are ever open at once.
//   3. Escape-to-close, which the legacy version does not have — a dialog you can only
//      leave with the mouse is a trap for keyboard users.

function Modal({ title, children, onClose, footer, size, contentClassName }) {
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    const handleBackdropClick = (event) => {
        // Only a click on the backdrop itself closes — not one that bubbled up from
        // inside the dialog.
        if (event.target === event.currentTarget) onClose();
    };

    return (
        <div
            onClick={handleBackdropClick}
            className="ops-v2 fixed inset-0 z-[200000] flex justify-center overflow-y-auto bg-[rgba(16,24,40,0.45)] p-3 sm:p-6"
            role="presentation"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="ops-modal-title"
                className={`my-auto flex max-h-[calc(100dvh-1.5rem)] min-h-0 w-full flex-col rounded-2xl bg-[var(--ops-carbon-25)] shadow-[var(--ops-shadow)] ${size} ${contentClassName}`}
            >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--ops-carbon-200)] px-5 py-4">
                    <strong
                        id="ops-modal-title"
                        className="pr-2 text-lg font-bold leading-snug text-[var(--ops-carbon-900)]"
                    >
                        {title}
                    </strong>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 rounded-lg p-1.5 text-[var(--ops-carbon-500)] transition-colors hover:bg-[var(--ops-carbon-100)] hover:text-[var(--ops-carbon-900)]"
                    >
                        <HugeiconsIcon icon={Cancel01Icon} size={16} strokeWidth={1.6} />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>

                {footer && (
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-[var(--ops-carbon-200)] px-5 py-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

Modal.propTypes = {
    title: PropTypes.node,
    children: PropTypes.node,
    onClose: PropTypes.func.isRequired,
    /** Optional action row pinned below the scrollable body. */
    footer: PropTypes.node,
    /** Tailwind max-width classes; override for wide content like the bill view. */
    size: PropTypes.string,
    contentClassName: PropTypes.string,
};

Modal.defaultProps = {
    title: null,
    children: null,
    footer: null,
    size: "max-w-lg sm:max-w-2xl md:max-w-3xl",
    contentClassName: "",
};

export default Modal;
